// =============================================================================
// SIREPPAR - Gestión de Expedientes (Vista Unificada)
// Flujo: PRESENTADO → ADMISIBILIDAD → ANÁLISIS → RESOLUCIÓN
// =============================================================================

import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import RichTextEditor, { MarkdownDisplay } from '../../components/common/RichTextEditor';
import { TIPO_TRAMITE_LABELS } from '../../utils/tiposExpediente';
import { sufijoBadgeNoPresentado } from '../../utils/noPresentado';
import { accionPendienteSecretario } from '../../utils/accionPendienteSecretario';
import { getFaseActual, getPlazoAfiliados, textoPlazoAfiliados } from '../../utils/fasesReconocimiento';

interface Documento {
  id: string;
  titulo: string;
  nombreOriginal: string;
  tipo: string;
  creadoEn: string;
  creadoPor?: string;
  estadoRevisionAdmisibilidad?: string;
  estadoRevisionAnalisis?: string;
  observacionRespuestaId?: string | null;
  intimacionRespuestaId?: string | null;
}

interface Observacion {
  id: string;
  tipo: string;
  estado: string;
  texto: string;
  creadoEn: string;
  fechaLimite?: string;
  plazoDias?: number;
  destinatario?: string;
  articulo?: string;
  respuestaTexto?: string;
  respuestaFecha?: string;
}

interface Expediente {
  id: string;
  numero: string;
  tipo: string;
  estado: string;
  fechaCreacion: string;
  fechaVencimiento?: string;
  partido?: { denominacion: string; sigla?: string; numero?: number; tipo?: string; clase?: string };
  creador?: { nombre: string; apellido: string; email: string };
  asignado?: { nombre: string; apellido: string };
  observaciones?: Observacion[];
  documentos?: Documento[];
  // Lleva la subfase del circuito "no presentado" (ver utils/noPresentado.ts).
  datosFormulario?: unknown;
  // Último movimiento que elevó el expediente al Secretario (el endpoint manda
  // solo ese). Sirve para contar los días que lleva sin resolverse.
  movimientos?: Array<{ fecha: string; estadoNuevo: string }>;
}

// Opciones del filtro "Tipo de partido". Los cuatro primeros son ámbitos territoriales
// (`Partido.tipo`); los dos últimos son clases de agrupación (`Partido.clase`).
const CLASES_AGRUPACION_FILTRO: Array<{ value: string; label: string }> = [
  { value: 'MUNICIPAL', label: 'Municipal' },
  { value: 'DISTRITO', label: 'Distrito' },
  { value: 'PROVINCIAL', label: 'Provincial' },
  { value: 'NACIONAL', label: 'Nacional' },
  { value: 'CONFEDERACION', label: 'Confederación' },
  { value: 'ALIANZA', label: 'Alianza' },
];

const VALORES_CLASE = ['CONFEDERACION', 'ALIANZA'];

/**
 * Un expediente pasa el filtro si el valor elegido matchea el ámbito del partido o su
 * clase, según a qué dimensión pertenezca el valor. Filtro vacío = pasa todo.
 */
function coincideTipoPartido(
  partido: { tipo?: string; clase?: string } | undefined,
  filtro: string,
): boolean {
  if (!filtro) return true;
  if (VALORES_CLASE.includes(filtro)) return partido?.clase === filtro;
  // Ámbito territorial: una confederación provincial entra en "Provincial", que es lo
  // que corresponde ahora que la clase y el ámbito son datos separados.
  return partido?.tipo === filtro;
}

const estadoConfig: Record<string, { color: string; label: string; etapa: string }> = {
  PRESENTADO: { color: 'bg-blue-100 text-blue-800', label: 'Presentado', etapa: 'admisibilidad' },
  EN_ADMISIBILIDAD: { color: 'bg-purple-100 text-purple-800', label: 'En Admisibilidad', etapa: 'admisibilidad' },
  INTIMADO: { color: 'bg-orange-100 text-orange-800', label: 'Intimado', etapa: 'admisibilidad' },
  EN_ANALISIS: { color: 'bg-yellow-100 text-yellow-800', label: 'En Análisis', etapa: 'analisis' },
  OBSERVADO: { color: 'bg-orange-100 text-orange-800', label: 'Observado', etapa: 'analisis' },
  // Legacy: nombre anterior de EN_SECRETARIO (expedientes sin migrar).
  PENDIENTE_RESOLUCION: { color: 'bg-indigo-100 text-indigo-800', label: 'En Secretario', etapa: 'resolucion' },
  APROBADO: { color: 'bg-green-100 text-green-800', label: 'Aprobado', etapa: 'finalizado' },
  RECHAZADO: { color: 'bg-red-100 text-red-800', label: 'Rechazado', etapa: 'finalizado' },
  // NO_PRESENTADO ya no es un estado terminal: es el circuito de resolución que se
  // abre cuando venció el plazo y el partido nunca subsanó (Secretario → TEP).
  // Vive en la columna Resolución junto a EN_SECRETARIO/EN_TEP, pero el badge rojo
  // deja claro que NO es un expediente correcto de esa etapa.
  NO_PRESENTADO: { color: 'bg-red-100 text-red-800', label: 'No Presentado', etapa: 'resolucion' },
  ARCHIVADO: { color: 'bg-gray-200 text-gray-600', label: 'Archivado', etapa: 'finalizado' },
  EN_SECRETARIO: { color: 'bg-indigo-100 text-indigo-800', label: 'En Secretario', etapa: 'resolucion' },
  EN_TEP: { color: 'bg-cyan-100 text-cyan-800', label: 'En TEP', etapa: 'resolucion' },
  REC_PROVISORIO: { color: 'bg-teal-100 text-teal-800', label: 'Reconocimiento provisorio', etapa: 'analisis' },
  VIGENTE: { color: 'bg-green-100 text-green-800', label: 'Vigente', etapa: 'finalizado' },
  CADUCO: { color: 'bg-gray-200 text-gray-600', label: 'Caduco', etapa: 'finalizado' },
  EXTINGUIDO: { color: 'bg-red-100 text-red-800', label: 'Extinguido', etapa: 'finalizado' },
};

const tipoLabels = TIPO_TRAMITE_LABELS;

type TabType = 'admisibilidad' | 'analisis' | 'resolucion' | 'finalizado' | 'todos';

// Opciones del filtro "Estado" según la tab activa.
// value 'TODOS' = sin filtro; 'CON_RESPUESTA' = expedientes con respuesta del partido pendiente
// de revisión; cualquier otro value se compara contra exp.estado.
const ESTADO_FILTROS: Record<TabType, { value: string; label: string }[]> = {
  admisibilidad: [
    { value: 'TODOS', label: 'Admisibilidad' },
    { value: 'INTIMADO', label: 'Intimado' },
    { value: 'CON_RESPUESTA', label: 'Con respuesta' },
  ],
  analisis: [
    { value: 'TODOS', label: 'Análisis' },
    { value: 'OBSERVADO', label: 'Observado' },
    { value: 'CON_RESPUESTA', label: 'Con respuesta' },
  ],
  resolucion: [
    { value: 'TODOS', label: 'Resolución' },
    { value: 'EN_SECRETARIO', label: 'En Secretario' },
    { value: 'EN_TEP', label: 'En TEP' },
    { value: 'NO_PRESENTADO', label: 'No presentado' },
  ],
  finalizado: [
    { value: 'TODOS', label: 'Finalizados' },
    { value: 'APROBADO', label: 'Aprobado' },
    { value: 'RECHAZADO', label: 'Rechazado' },
    { value: 'VIGENTE', label: 'Vigente' },
    { value: 'ARCHIVADO', label: 'Archivado' },
    { value: 'CADUCO', label: 'Caduco' },
    { value: 'EXTINGUIDO', label: 'Extinguido' },
  ],
  todos: [{ value: 'TODOS', label: 'Todos' }],
};

function HistorialConPdfs({
  observaciones,
  documentos,
  expedienteNumero,
}: {
  observaciones?: Observacion[];
  documentos: Documento[];
  expedienteNumero: string;
}) {
  if (!observaciones?.length) {
    return <p className="text-gray-500 text-sm">Sin observaciones registradas</p>;
  }

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const getPdfObservacion = (obs: Observacion) =>
    documentos.find((d) => {
      if (d.tipo !== 'NOTA' || !d.titulo?.includes(expedienteNumero)) return false;
      const diff = Math.abs(new Date(d.creadoEn).getTime() - new Date(obs.creadoEn).getTime());
      return diff < 60_000;
    });

  // Documentos que el partido adjuntó al responder esta observación/intimación,
  // vinculados de forma exacta (no por heurística de título/fecha).
  const getDocsRespuesta = (obs: Observacion) => {
    const esIntimacion = obs.tipo === 'INTIMACION';
    return documentos.filter((d) =>
      esIntimacion ? d.intimacionRespuestaId === obs.id : d.observacionRespuestaId === obs.id,
    );
  };

  const abrirDoc = async (docId: string) => {
    try {
      const res = await api.get(`/documentos/${docId}/url`);
      const url = res.data.data?.url || res.data.url;
      if (url) window.open(url, '_blank');
    } catch {
      toast.error('No se pudo abrir el documento');
    }
  };

  return (
    <div className="space-y-3">
      {observaciones.map((obs) => {
        const pdfObs = getPdfObservacion(obs);
        const docsResp = getDocsRespuesta(obs);
        const esIntimacion = obs.tipo === 'INTIMACION';

        return (
          <div key={obs.id} className="space-y-2">
            <div className="flex gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${esIntimacion ? 'bg-orange-100' : 'bg-yellow-100'}`}>
                <span className={`text-xs font-bold ${esIntimacion ? 'text-orange-600' : 'text-yellow-700'}`}>S</span>
              </div>
              <div className={`flex-1 rounded-lg p-3 border ${esIntimacion ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'}`}>
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold ${esIntimacion ? 'text-orange-800' : 'text-yellow-800'}`}>
                      Secretaría Electoral
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${esIntimacion ? 'bg-orange-200 text-orange-700' : 'bg-yellow-200 text-yellow-700'}`}>
                      {obs.tipo}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">{fmt(obs.creadoEn)}</span>
                </div>
                <div className="text-sm text-gray-700"><MarkdownDisplay content={obs.texto} /></div>
                {obs.plazoDias && (
                  <p className="text-xs mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${obs.destinatario === 'SEP' ? 'bg-blue-100 text-blue-800' : obs.destinatario === 'PARTIDO' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'}`}>
                      {obs.destinatario === 'SEP' ? 'Pendiente de resolución SEP' : obs.destinatario === 'PARTIDO' ? 'Esperando respuesta del partido' : 'Esperando respuesta del afiliado'}
                      {obs.fechaLimite ? ` (${Math.max(0, Math.ceil((new Date(obs.fechaLimite).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} días restantes)` : ''}
                    </span>
                    {obs.plazoDias && <span className="text-gray-400 ml-2">{obs.plazoDias} días hábiles · {obs.articulo}{obs.fechaLimite ? ` · Límite: ${new Date(obs.fechaLimite).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : ''}</span>}
                  </p>
                )}
                {pdfObs && (
                  <button
                    onClick={() => abrirDoc(pdfObs.id)}
                    className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded border bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                    title={pdfObs.nombreOriginal}
                  >
                    📄 {pdfObs.titulo || pdfObs.nombreOriginal}
                  </button>
                )}
              </div>
            </div>

            {obs.respuestaTexto && (
              <div className="flex gap-2 pl-6">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-xs font-bold">A</span>
                </div>
                <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-xs font-semibold text-blue-800">Apoderado</span>
                    <span className="text-xs text-gray-400">
                      {obs.respuestaFecha ? fmt(obs.respuestaFecha) : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-snug">{obs.respuestaTexto}</p>
                  <p className="mt-2 text-xs font-medium text-blue-700">
                    {docsResp.length > 0
                      ? `📎 ${docsResp.length} documento${docsResp.length === 1 ? '' : 's'} adjunto${docsResp.length === 1 ? '' : 's'}`
                      : 'Sin documentos adjuntos'}
                  </p>
                </div>
              </div>
            )}

            {obs.estado === 'RESPONDIDA' && (
              <div className="pl-6">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  ✓ Respondida
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function GestionExpedientesPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tabInicial: TabType = (['admisibilidad', 'analisis', 'resolucion', 'todos'] as TabType[])
    .includes(tabParam as TabType)
    ? (tabParam as TabType)
    : 'admisibilidad';
  const [tab, setTab] = useState<TabType>(tabInicial);
  const [filtroEstado, setFiltroEstado] = useState<string>('TODOS'); // estado del expediente, opciones según la tab
  const [filtroTipo, setFiltroTipo] = useState<string>('TODOS'); // tipo de TRÁMITE (RENDICION_ANUAL, REEMPADRONAMIENTO, ...)
  // Filtro "Tipo de partido". Mezcla dos dimensiones a propósito, porque para el usuario
  // son una sola pregunta: los valores territoriales (MUNICIPAL, DISTRITO, PROVINCIAL,
  // NACIONAL) matchean contra `partido.tipo`, y CONFEDERACION / ALIANZA contra
  // `partido.clase` (ver ClaseAgrupacion). Antes CONFEDERACION era un `TipoPartido` y el
  // filtro comparaba una sola columna.
  const [filtroTipoPartido, setFiltroTipoPartido] = useState<string>('');
  const [modalAccion, setModalAccion] = useState<{ id: string; tipo: 'intimar' | 'observar' | 'rechazar' } | null>(null);
  const [textoObservacion, setTextoObservacion] = useState('');
  const [expedienteDetalle, setExpedienteDetalle] = useState<string | null>(null);

  // Query todos los expedientes activos
  const { data, isLoading } = useQuery({
    queryKey: ['gestion-expedientes'],
    queryFn: async () => {
      const res = await api.get('/expedientes/gestion');
      return res.data.data;
    },
  });

  // Mutations
  const iniciarAdmisibilidadMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admisibilidad/${id}/iniciar`),
    onSuccess: () => {
      toast.success('Admisibilidad iniciada');
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  // Para rendiciones: saltea admisibilidad y va directo a análisis
  const iniciarAnalisisMutation = useMutation({
    mutationFn: (id: string) => api.post(`/expedientes/${id}/iniciar-analisis`),
    onSuccess: () => {
      toast.success('Análisis iniciado');
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const TIPOS_SIN_ADMISIBILIDAD = ['RENDICION_ANUAL', 'RENDICION_CAMPANA'];

  const aprobarAdmisibilidadMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admisibilidad/${id}/aprobar`),
    onSuccess: () => {
      toast.success('Admisibilidad aprobada - Pasa a Análisis');
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const intimarMutation = useMutation({
    mutationFn: ({ id, observaciones }: { id: string; observaciones: string }) =>
      api.post(`/admisibilidad/${id}/intimar`, { observaciones }),
    onSuccess: () => {
      toast.success('Intimación enviada');
      setModalAccion(null);
      setTextoObservacion('');
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const subsanarMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admisibilidad/${id}/subsanar`),
    onSuccess: () => {
      toast.success('Marcado como subsanado');
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const noPresentadoMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admisibilidad/${id}/no-presentado`),
    onSuccess: () => {
      toast.success('Tenido por no presentado');
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const observarMutation = useMutation({
    mutationFn: ({ id, texto }: { id: string; texto: string }) =>
      api.post(`/expedientes/${id}/observar`, { texto }),
    onSuccess: () => {
      toast.success('Observación enviada');
      setModalAccion(null);
      setTextoObservacion('');
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });


  const expedientes: Expediente[] = data || [];

  // Verificar si tiene respuesta pendiente de revisión
  const tieneRespuestaPendiente = (exp: Expediente) => {
    if (!['INTIMADO', 'OBSERVADO'].includes(exp.estado)) return false;
    if (exp.observaciones?.some(o => o.estado === 'PENDIENTE')) return false;
    return exp.observaciones?.some(o => o.estado === 'RESPONDIDA') ?? false;
  };

  // Filtrar por etapa
  const filtrarPorEtapa = (etapa: TabType) => {
    const base = etapa === 'todos' ? expedientes : expedientes.filter(e => estadoConfig[e.estado]?.etapa === etapa);
    if (filtroTipo === 'TODOS') return base;
    return base.filter(e => e.tipo === filtroTipo);
  };

  // "Con respuesta": el expediente tiene al menos una respuesta del partido, sin importar
  // si quedan otras observaciones pendientes. Misma definición que el contador de la tabla.
  const tieneAlgunaRespuesta = (exp: Expediente) =>
    (exp.observaciones || []).some(o => !!o.respuestaTexto || o.estado === 'RESPONDIDA');

  // Filtro "Estado" (dependiente de la tab): 'TODOS' no filtra, 'CON_RESPUESTA'
  // lista los que tienen alguna respuesta, y cualquier otro value compara contra exp.estado.
  const aplicarFiltroEstado = (lista: Expediente[]) => {
    if (filtroEstado === 'TODOS') return lista;
    if (filtroEstado === 'CON_RESPUESTA') return lista.filter(tieneAlgunaRespuesta);
    return lista.filter(e => e.estado === filtroEstado);
  };

  const expedientesFiltrados = aplicarFiltroEstado(
    filtrarPorEtapa(tab).filter(e => coincideTipoPartido(e.partido, filtroTipoPartido))
  );

  // Contar por etapa (sin aplicar filtro de tipo, así los counters reflejan la cola completa)
  const contarEtapa = (etapa: string) =>
    expedientes.filter(e => estadoConfig[e.estado]?.etapa === etapa).length;

  // Tipos presentes en la lista (para llenar el dropdown solo con los que existen)
  const tiposPresentes = Array.from(new Set(expedientes.map(e => e.tipo))).sort();

  // ---------------------------------------------------------------------------
  // Contadores de la columna ALERTAS
  // ---------------------------------------------------------------------------
  // Los contadores son POR ETAPA: en admisibilidad se cuentan intimaciones, en
  // análisis observaciones. Un contador de respuestas sin el total de lo cursado
  // no se puede leer ("3 respuestas" ¿sobre cuántas?), así que van juntos.
  // 'AMBAS' se usa solo en Resolución: al Secretario le interesa el total de lo que
  // se le cursó al partido, sin distinguir la etapa de origen.
  type EtapaConteo = 'INTIMACION' | 'OBSERVACION' | 'AMBAS';

  // Qué se cuenta en cada fila. Devuelve null cuando no corresponde mostrar nada.
  const etapaConteoDe = (exp: Expediente): EtapaConteo | null => {
    // En Resolución solo se muestran para los NO_PRESENTADO, y con la etapa de la
    // que vienen: así el Secretario ve de un golpe cuántas intimaciones u
    // observaciones quedaron sin corregir, que es el motivo de que estén en su
    // columna. El resto de los expedientes en resolución no tiene nada pendiente
    // que contar.
    // Se cuentan las intimaciones Y las observaciones juntas: el expediente pudo
    // pasar por las dos etapas y al Secretario le importa el total de lo que el
    // partido dejó sin corregir, no de qué etapa viene cada una. Se rotulan como
    // "observaciones" para no llenar la celda de contadores.
    if (exp.estado === 'NO_PRESENTADO') return 'AMBAS';
    if (tab === 'admisibilidad') return 'INTIMACION';
    if (tab === 'analisis') return 'OBSERVACION';
    if (tab === 'resolucion') return null;
    // Tab "todos": se deduce del estado del expediente.
    if (['PRESENTADO', 'EN_ADMISIBILIDAD', 'INTIMADO'].includes(exp.estado)) return 'INTIMACION';
    if (['EN_ANALISIS', 'OBSERVADO'].includes(exp.estado)) return 'OBSERVACION';
    return null;
  };

  const contadoresEtapa = (exp: Expediente, etapa: EtapaConteo) => {
    const items = (exp.observaciones || []).filter(o =>
      etapa === 'AMBAS' ? true
      : etapa === 'INTIMACION' ? o.tipo === 'INTIMACION'
      : o.tipo !== 'INTIMACION',
    );
    const respuestas = items.filter(o => !!o.respuestaTexto || o.estado === 'RESPONDIDA').length;
    // "Sin revisar" mira la dimensión de verificación que corresponde a la etapa
    // (admisibilidad para intimaciones, análisis para observaciones), CON UNA
    // EXCEPCIÓN: RECONOCIMIENTO verifica siempre en la dimensión de admisibilidad
    // (es la única que usa ese trámite, ver dimensionRevision en DocumentosPage).
    // Sin este caso, la supervisora aprobaba el adjunto —que escribe la dimensión
    // de admisibilidad— y este contador leía la de análisis, que queda en PENDIENTE
    // para siempre: el expediente mostraba "1 sin revisar" eternamente.
    const esRecon = exp.tipo === 'RECONOCIMIENTO';
    const sinRevisar = items.filter(o => {
      // La vinculación y la dimensión se resuelven por ÍTEM (no por la etapa del
      // tab), porque en 'AMBAS' conviven intimaciones y observaciones.
      const esIntimacionItem = o.tipo === 'INTIMACION';
      const docsResp = (exp.documentos || []).filter(d =>
        esIntimacionItem ? d.intimacionRespuestaId === o.id : d.observacionRespuestaId === o.id,
      );
      return docsResp.some(d =>
        ((esRecon || esIntimacionItem) ? d.estadoRevisionAdmisibilidad : d.estadoRevisionAnalisis) === 'PENDIENTE',
      );
    }).length;
    return { total: items.length, respuestas, sinRevisar };
  };

  const diasHabilesDesde = (fecha: string) => {
    const totalDias = Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));
    return Math.floor(totalDias * 5 / 7);
  };

  // ---------------------------------------------------------------------------
  // Columna DÍAS
  // ---------------------------------------------------------------------------
  // El plazo se lee distinto según de quién sea la pelota, así que el texto lo
  // dice explícitamente en lugar de un "X días restantes" ambiguo:
  //   - del partido  → "X días sin responder"
  //   - de la SEP    → "X días para revisar" (plazo de la etapa corriendo)
  //   - ya respondió → "X días sin revisar" (desde la respuesta del partido)
  //   - pasado       → "X días de atraso"
  //
  // Devuelve null cuando no hay NADA que contar (p. ej. BORRADOR): la columna
  // muestra "—". Que no haya PLAZO no es lo mismo que no haya nada que mostrar:
  //   1. RECONOCIMIENTO en fases de la SEP (revisión de atributos y de
  //      documentación): la Ley XII N° 23 no le fija plazo a la SEP. El Art. 10
  //      solo da 5 días a terceros para oponerse y 10 días al TEP para fijar
  //      audiencia; los Art. 18/19 (documentación) no fijan ninguno. Por eso el
  //      expediente se crea con fechaVencimiento = null. Igual se muestra
  //      "X días sin resolver" (informativo, nunca en rojo) — ver caso 5.
  //   2. EN_SECRETARIO / EN_TEP / PENDIENTE_RESOLUCION: no hay plazo reglamentado
  //      para que el Secretario o el TEP resuelvan, así que NO se mide vencimiento;
  //      lo que se muestra es cuánto lleva el expediente sin resolverse desde que
  //      pasó a esa etapa ("X días sin resolver"). Sin este corte se seguiría
  //      mostrando el vencimiento de la etapa ANTERIOR (los 30 días de verificación
  //      administrativa), marcando atraso sobre un plazo que ya no corre.
  const diasDesde = (fecha: string) =>
    Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));

  const diasHasta = (fecha: string) =>
    Math.ceil((new Date(fecha).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  const plural = (n: number) => (n === 1 ? 'día' : 'días');

  // Fecha del último movimiento hacia alguno de esos estados. El endpoint manda
  // los movimientos a EN_SECRETARIO/EN_TEP (los más recientes primero), así que
  // acá se elige el que corresponde al estado en el que está el expediente: si fue
  // y volvió (elevado al TEP → devuelto → vuelto a elevar), el contador arranca en
  // la última vez, no en la primera.
  const ultimoMovimientoA = (exp: Expediente, estados: string[]): string | undefined =>
    exp.movimientos?.find(m => estados.includes(m.estadoNuevo))?.fecha;

  // Fecha de la respuesta MÁS RECIENTE del partido (sobre cualquier intimación u
  // observación). Es el punto desde el que se cuentan los días que la SEP lleva sin
  // revisar. Las respuestas viejas no importan: si el partido responde una segunda
  // vez, el contador arranca de nuevo ahí.
  // `respuestaFecha` es lo que sella el backend al responder; si faltara (respuestas
  // cargadas sin fecha), se usa el adjunto más nuevo vinculado a esa respuesta.
  const fechaUltimaRespuesta = (exp: Expediente): string | undefined => {
    const fechas = (exp.observaciones || [])
      .filter(o => !!o.respuestaTexto || o.estado === 'RESPONDIDA')
      .map(o => {
        if (o.respuestaFecha) return o.respuestaFecha;
        const adjuntos = (exp.documentos || [])
          .filter(d => d.observacionRespuestaId === o.id || d.intimacionRespuestaId === o.id)
          .map(d => d.creadoEn);
        return adjuntos.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
      })
      .filter((f): f is string => !!f);
    return fechas.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  };

  // Plazo de revisión de la SEP: mide contra la fechaVencimiento de la etapa.
  const plazoRevisionSep = (fechaLimite: string): { texto: string; vencido: boolean } => {
    const dias = diasHasta(fechaLimite);
    if (dias < 0) {
      const n = Math.abs(dias);
      return { texto: `${n} ${plural(n)} de atraso`, vencido: true };
    }
    if (dias === 0) return { texto: 'Vence hoy para revisar', vencido: false };
    return { texto: `${dias} ${plural(dias)} para revisar`, vencido: false };
  };

  // Estados en los que la SEP tiene un plazo propio de revisión (Resolución
  // N° 002/2026 I-SEP: 5 días de control de admisibilidad, 30 de verificación
  // administrativa). Fuera de estos, el vencimiento guardado ya no corre.
  const ESTADOS_CON_PLAZO_SEP = ['PRESENTADO', 'EN_ADMISIBILIDAD', 'EN_ANALISIS'];

  const plazoDeExpediente = (exp: Expediente): { texto: string; vencido: boolean } | null => {
    // 1. INTIMADO / OBSERVADO: lo que importa es cuánto lleva el partido sin
    //    contestar, así que se cuenta desde que se cursó la intimación u
    //    observación más antigua que sigue sin respuesta (no desde su vencimiento).
    if (exp.estado === 'INTIMADO' || exp.estado === 'OBSERVADO') {
      const sinResponder = (exp.observaciones || [])
        .filter(o => o.estado === 'PENDIENTE')
        .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime())[0];
      if (sinResponder) {
        const n = Math.max(0, diasDesde(sinResponder.creadoEn));
        // Se marca en rojo recién cuando además venció el plazo otorgado.
        const vencido = !!sinResponder.fechaLimite && diasHasta(sinResponder.fechaLimite) < 0;
        return { texto: `${n} ${plural(n)} sin responder`, vencido };
      }
      // 1.bis Ya no queda nada sin responder pero el expediente sigue INTIMADO /
      //    OBSERVADO: la pelota volvió a la SEP y todavía no resolvió (es la fila
      //    verde con la alerta "el partido respondió"). Se cuenta desde la respuesta
      //    MÁS RECIENTE, así el contador se reinicia solo:
      //      - si la SEP acepta la subsanación, el expediente cambia de estado y
      //        deja de entrar por acá;
      //      - si vuelve a intimar u observar, la nueva queda PENDIENTE y la fila
      //        pasa al caso de arriba ("X días sin responder");
      //      - si el partido responde de nuevo, la fecha más reciente es la nueva.
      const ultimaRespuesta = fechaUltimaRespuesta(exp);
      if (!ultimaRespuesta) return null;
      const n = Math.max(0, diasDesde(ultimaRespuesta));
      // No hay plazo propio para revisar una respuesta: se marca en rojo si además
      // ya venció el plazo de la etapa (los 30 días de verificación administrativa).
      const vencido = !!exp.fechaVencimiento && diasHasta(exp.fechaVencimiento) < 0;
      return { texto: `${n} ${plural(n)} sin revisar`, vencido };
    }

    // 2. REC_PROVISORIO: el partido tiene 4 meses desde la resolución del TEP para
    //    presentar el padrón de afiliados. El vencimiento lo guarda el backend en
    //    datosFormulario al registrar el acta, así que se cuenta contra esa fecha.
    if (exp.estado === 'REC_PROVISORIO') {
      const plazo = getPlazoAfiliados(exp.datosFormulario);
      if (!plazo) return null;
      return { texto: textoPlazoAfiliados(plazo), vencido: plazo.vencido };
    }

    // 3. EN_SECRETARIO: esta etapa no tiene plazo reglamentado, así que no hay
    //    contra qué medir un vencimiento; se cuenta cuánto lleva sin resolverse
    //    desde el movimiento que lo elevó al Secretario.
    if (exp.estado === 'EN_SECRETARIO' || exp.estado === 'PENDIENTE_RESOLUCION') {
      const desde = ultimoMovimientoA(exp, ['EN_SECRETARIO', 'PENDIENTE_RESOLUCION']);
      if (!desde) return null;
      const n = Math.max(0, diasDesde(desde));
      return { texto: `${n} ${plural(n)} sin resolver`, vencido: n > 30 };
    }

    // 3.bis EN_TEP: igual que el Secretario, el TEP no tiene plazo reglamentado
    //    para resolver, pero interesa saber cuánto lleva el expediente elevado.
    //    Se cuenta desde el movimiento que lo elevó al TEP.
    if (exp.estado === 'EN_TEP') {
      const desde = ultimoMovimientoA(exp, ['EN_TEP']);
      if (!desde) return null;
      const n = Math.max(0, diasDesde(desde));
      return { texto: `${n} ${plural(n)} sin resolver`, vencido: n > 30 };
    }

    // 4. Etapas con plazo propio de la SEP.
    if (ESTADOS_CON_PLAZO_SEP.includes(exp.estado) && exp.fechaVencimiento) {
      return plazoRevisionSep(exp.fechaVencimiento);
    }

    // 5. Etapas de la SEP SIN plazo reglamentado (es el caso de RECONOCIMIENTO: la
    //    Ley XII N° 23 no le fija plazo a la SEP ni para los atributos ni para la
    //    documentación, así que el expediente se crea con fechaVencimiento = null).
    //    No hay vencimiento que medir, pero sí interesa cuánto lleva el expediente
    //    en la etapa sin resolverse: se cuenta desde el movimiento que lo dejó en
    //    ese estado, o desde la presentación si no hay ninguno. No se marca en rojo
    //    porque no hay plazo incumplido: es informativo.
    if (ESTADOS_CON_PLAZO_SEP.includes(exp.estado)) {
      const desde = ultimoMovimientoA(exp, [exp.estado]) ?? exp.fechaCreacion;
      const n = Math.max(0, diasDesde(desde));
      return { texto: `${n} ${plural(n)} sin resolver`, vencido: false };
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Expedientes</h1>
        <p className="text-gray-500 mt-1">
          Flujo completo: Admisibilidad → Análisis → Resolución
        </p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <p className="text-sm text-gray-500">Admisibilidad</p>
          <p className="text-2xl font-bold">{contarEtapa('admisibilidad')}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
          <p className="text-sm text-gray-500">Análisis</p>
          <p className="text-2xl font-bold">{contarEtapa('analisis')}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
          <p className="text-sm text-gray-500">Resolución</p>
          <p className="text-2xl font-bold">{contarEtapa('resolucion')}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <p className="text-sm text-gray-500">Finalizados</p>
          <p className="text-2xl font-bold">{contarEtapa('finalizado')}</p>
        </div>
      </div>

      {/* Tabs por etapa */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'admisibilidad', label: 'Admisibilidad', color: 'purple' },
            { key: 'analisis', label: 'Análisis', color: 'yellow' },
            { key: 'resolucion', label: 'Resolución', color: 'indigo' },
            { key: 'finalizado', label: 'Finalizados', color: 'green' },
            { key: 'todos', label: 'Todos', color: 'gray' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key as TabType); setFiltroEstado('TODOS'); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                tab === t.key
                  ? `border-${t.color}-500 text-${t.color}-600`
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label} ({t.key === 'todos' ? expedientes.length : contarEtapa(t.key)})
            </button>
          ))}
        </nav>
      </div>
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Estado:</label>
          <select
            value={filtroEstado}
            onChange={e => setFiltroEstado(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ESTADO_FILTROS[tab].map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Tipo de partido:</label>
          <select
            value={filtroTipoPartido}
            onChange={e => setFiltroTipoPartido(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los tipos</option>
            {CLASES_AGRUPACION_FILTRO.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Tipo de trámite:</label>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="TODOS">Todos</option>
            {tiposPresentes.map((t) => (
              <option key={t} value={t}>{tipoLabels[t] ?? t}</option>
            ))}
          </select>
          {filtroTipo !== 'TODOS' && (
            <span className="text-xs text-gray-500">
              ({expedientesFiltrados.length} expediente{expedientesFiltrados.length !== 1 ? 's' : ''})
            </span>
          )}
        </div>
      </div>

      {/* Lista de expedientes */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : expedientesFiltrados.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay expedientes en esta etapa</div>
        ) : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-64">Partido</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alertas</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expedientesFiltrados.map((exp) => (
                <Fragment key={exp.id}>
                <tr className={tieneRespuestaPendiente(exp) ? 'bg-green-50' : (exp.estado === 'PRESENTADO' && diasHabilesDesde(exp.fechaCreacion) > 5 ? 'bg-red-50' : '')}>
                  <td className="px-4 py-3">
                    <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                      {exp.numero}
                    </Link>
                    <p className="text-xs text-gray-500">{tipoLabels[exp.tipo] || exp.tipo}</p>
                  </td>
                  <td className="px-4 py-3 w-64 max-w-[16rem]">
                    <p className="text-sm font-medium break-words">{exp.partido?.denominacion}</p>
                    <p className="text-xs text-gray-500 break-words">{exp.partido?.sigla}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${estadoConfig[exp.estado]?.color}`}>
                      {estadoConfig[exp.estado]?.label || exp.estado}
                      {/* En NO_PRESENTADO el estado no alcanza: hay que ver si la
                          pelota la tiene el Secretario o el TEP. */}
                      {exp.estado === 'NO_PRESENTADO' && sufijoBadgeNoPresentado(exp.datosFormulario)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const plazo = plazoDeExpediente(exp);
                      if (!plazo) {
                        return (
                          <span className="text-gray-400" title="Esta etapa no tiene plazo reglamentado">—</span>
                        );
                      }
                      return (
                        <span className={`text-xs font-medium ${plazo.vencido ? 'text-red-600 font-bold' : 'text-gray-700'}`}>
                          {plazo.texto}
                        </span>
                      );
                    })()}
                  </td>
                  {/* ALERTAS: solo lo que exige una acción concreta de la SEP. Los
                      badges de "en control de admisibilidad" / "esperando respuesta
                      del partido" se quitaron: repetían la columna ESTADO y la de
                      DÍAS, y tapaban las alertas que importan acá. */}
                  <td className="px-4 py-3 space-y-1">
                    {/* Qué le falta hacer al Secretario. Va PRIMERO porque es lo que
                        busca al entrar, y en TEXTO PLANO SIN COLOR: no es una alerta,
                        es la tarea — el color queda para lo que sí es urgente. */}
                    {tab === 'resolucion' && (() => {
                      const pendiente = accionPendienteSecretario(exp);
                      return pendiente ? <p className="text-[13px] text-gray-700">{pendiente}</p> : null;
                    })()}
                    {/* Publicar los atributos es la única acción del Secretario que
                        abre un plazo hacia AFUERA (los 5 días de oposición del Art.
                        10): lleva tag propio para que la encuentre de un golpe entre
                        las elevaciones al TEP. */}
                    {tab === 'resolucion' && getFaseActual(exp.datosFormulario) === 'ATRIBUTOS_A_PUBLICAR' && (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 block w-fit font-semibold">
                        A PUBLICAR
                      </span>
                    )}
                    {exp.estado === 'PRESENTADO' && diasHabilesDesde(exp.fechaCreacion) > 5 && (
                      <span className="px-2 py-1 text-xs rounded-lg block w-fit bg-red-100 text-red-700 font-semibold">
                        Sin iniciar control de admisibilidad
                      </span>
                    )}
                    {tieneRespuestaPendiente(exp) && (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 block w-fit font-semibold">
                        Partido respondió
                      </span>
                    )}
                    {(() => {
                      const etapa = etapaConteoDe(exp);
                      if (!etapa) return null;
                      const { total, respuestas, sinRevisar } = contadoresEtapa(exp, etapa);
                      if (total === 0) return null;
                      const esIntimacion = etapa === 'INTIMACION';
                      const labelTotal = esIntimacion
                        ? (total === 1 ? 'intimación' : 'intimaciones')
                        : (total === 1 ? 'observación' : 'observaciones');
                      // En Resolución el rótulo dice "observaciones" pero el total
                      // incluye las intimaciones: el tooltip lo aclara.
                      const tituloTotal = etapa === 'AMBAS'
                        ? 'Total de intimaciones y observaciones cursadas al partido'
                        : `Total de ${esIntimacion ? 'intimaciones' : 'observaciones'} cursadas al partido`;
                      const tituloRespuestas = etapa === 'AMBAS'
                        ? 'Respuestas del partido a las intimaciones y observaciones'
                        : `Respuestas del partido a las ${esIntimacion ? 'intimaciones' : 'observaciones'}`;
                      return (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px]">
                          <span
                            className="text-gray-700 font-bold"
                            title={tituloTotal}
                          >
                            {total} {labelTotal}
                          </span>
                          <span
                            className={respuestas === 0 ? 'text-gray-400 font-bold' : 'text-green-700 font-bold'}
                            title={tituloRespuestas}
                          >
                            {respuestas} respondida{respuestas === 1 ? '' : 's'}
                          </span>
                          {sinRevisar > 0 && (
                            <span className="text-red-600 font-bold" title="Respuestas con adjuntos sin verificar">
                              {sinRevisar} sin revisar
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                      {/* Botón ver historial */}
                      <button
                        onClick={() => setExpedienteDetalle(expedienteDetalle === exp.id ? null : exp.id)}
                        className="px-2 py-1 text-gray-600 text-sm hover:bg-gray-100 rounded"
                      >
                        {expedienteDetalle === exp.id ? '▼' : '▶'} Historial
                      </button>

                      {/* Gestionar: lleva al detalle del expediente */}
                      <Link
                        to={`/expedientes/${exp.id}`}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Gestionar
                      </Link>
                    </div>
                  </td>
                </tr>
                {expedienteDetalle === exp.id && (
  <tr className="bg-gray-50">
    <td colSpan={6} className="px-6 py-4">
      <p className="font-semibold text-sm text-gray-700 mb-3">
        Historial de Observaciones/Intimaciones
      </p>
      <HistorialConPdfs
        observaciones={exp.observaciones}
        documentos={exp.documentos || []}
        expedienteNumero={exp.numero}
      />
    </td>
  </tr>
)}
                </Fragment>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Modal para Intimar/Observar */}
      {modalAccion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
          <h3 className="text-lg font-bold mb-4">
            {modalAccion.tipo === 'intimar'
              ? (expedientes.find(e => e.id === modalAccion.id)?.estado === 'INTIMADO'
                  ? 'Agregar observación a intimación existente'
                  : 'Intimar por Documentación Incompleta')
              : 'Observar Expediente'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {modalAccion.tipo === 'intimar'
                ? (expedientes.find(e => e.id === modalAccion.id)?.estado === 'INTIMADO'
                    ? 'Se agregará una nueva observación. El plazo del partido se reiniciará desde hoy (10 días hábiles).'
                    : 'El partido tendrá 10 días hábiles para subsanar.')
                : 'Detalle las observaciones que debe corregir el partido.'}
            </p>
            <RichTextEditor
              value={textoObservacion}
              onChange={setTextoObservacion}
              placeholder="Detalle las observaciones..."
              minHeight={150}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => { setModalAccion(null); setTextoObservacion(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (modalAccion.tipo === 'intimar') {
                    intimarMutation.mutate({ id: modalAccion.id, observaciones: textoObservacion });
                  } else {
                    observarMutation.mutate({ id: modalAccion.id, texto: textoObservacion });
                  }
                }}
                disabled={!textoObservacion.trim() || intimarMutation.isPending}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {intimarMutation.isPending ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
